/* eslint-disable jsx-a11y/anchor-is-valid */
import { type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheckIcon, LockIcon, SquareCheckIcon } from '@cx-web/components/primitives/icons';
import { useRepubContext } from '@cx-web/feature-repub';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { Divider } from '@fpsm/cxy-components/Divider';

import { REPUBS } from '@cx-modules/constants';

import {
  useAuthModal,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_EMAIL,
  VIEW_VERIFY_PAYMENT,
} from '../auth-modal-provider';

import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { SocialButtons } from './components/SocialButtons';
import { VerifyEmail } from './components/VerifyEmail';
import { VerifyPayment } from './components/VerifyPayment';
import styles from './styles/Authentication.module.css';

export type InfoSectionProps = {
  resourcesUrl: string;
  view: View;
};

export const InfoLink = () => {
  const { repubPath } = useRepubContext();
  return (
    <p className={styles.infoLink}>
      For more information <a href={`${repubPath}/plus`}>click here</a>
    </p>
  );
};

const FEATURE_LIST_ICONS = {
  'circle-check': <CircleCheckIcon />,
  'square-check': <SquareCheckIcon />,
};

const FeatureList = ({
  featureList,
}: {
  featureList: {
    title: string;
  }[];
}) => {
  const { repub } = useRepubContext();
  const iconKey = REPUB_CONFIG[repub]?.plus?.auth.featureListIcon;

  return (
    <ul className={styles.featureList}>
      {featureList.map((feature, index) => (
        <li key={index}>
          {iconKey && FEATURE_LIST_ICONS[iconKey]}
          <span>{feature.title}</span>
        </li>
      ))}
    </ul>
  );
};

export const InfoSection = ({ children, resourcesUrl }: PropsWithChildren<InfoSectionProps>) => {
  const { t } = useTranslation(['label']);
  const { repub } = useRepubContext();

  // Get config for current repub, fallback to OC
  const config = REPUB_CONFIG[repub]?.plus || REPUB_CONFIG[REPUBS.OC.name]?.plus;

  return (
    <div className={styles.infoSection} data-testid="info-section">
      <div>
        <div className={styles.logoContainer}>
          <img alt={config?.auth.logoAlt} src={config?.auth.logoSrc(resourcesUrl)} />
        </div>
        <h2 className={styles.header}>{config?.auth.header(t)}</h2>
        <p>{config?.auth.tagline(t)}</p>
        <FeatureList featureList={config?.auth.featureList ?? []} />
        {children}
      </div>
    </div>
  );
};

InfoSection.InfoLink = InfoLink;

export type AuthSectionProps = { currentUrl: string; googleID: string; view: View };

const AuthSection = ({ currentUrl, googleID, view }: AuthSectionProps) => {
  const { repub, repubPath } = useRepubContext();
  const { close, openAtView, prefillEmail } = useAuthModal();

  const isForgotPassword = view === VIEW_FORGOT_PASSWORD;
  const isSignup = view === VIEW_SIGNUP;
  const isVerifyingEmail = view === VIEW_VERIFY_EMAIL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const { t } = useTranslation(['label']);

  return (
    <div className={styles.authSection} data-testid="auth-section">
      <div className="flex flex-col space-y-2">
        {!isVerifyingEmail && (
          <h2>
            {!isVerifyingPayment && <LockIcon />}
            {isForgotPassword
              ? t('CX_AUTH_FORGOT_PASSWORD')
              : isSignup
                ? t('CX_AUTH_SIGN_UP_TO_UNLOCK')
                : isVerifyingPayment
                  ? t('CX_AUTH_ONE_MOMENT')
                  : t('CX_AUTH_LOGIN_TO_UNLOCK')}
          </h2>
        )}
        {isVerifyingPayment ? (
          <VerifyPayment />
        ) : isVerifyingEmail ? (
          <VerifyEmail />
        ) : isForgotPassword ? (
          <ForgotPasswordForm repubPath={repubPath} />
        ) : (
          <>
            {REPUB_CONFIG[repub]?.plus?.auth.showSocialLogin && (
              <>
                <SocialButtons currentUrl={currentUrl} googleID={googleID} repubPath={repubPath} view={view} />
                <Divider text="OR" />
              </>
            )}
            {isSignup ? (
              <SignupForm
                close={close}
                openAtView={openAtView}
                prefillEmail={prefillEmail}
                repub={repub}
                repubPath={repubPath}
              />
            ) : (
              <LoginForm close={close} openAtView={openAtView} repub={repub} repubPath={repubPath} />
            )}
          </>
        )}
        {(isForgotPassword || isSignup) && (
          <p className={styles.switchAuthText}>
            {isSignup ? `${t('CX_AUTH_ALREADY_HAVE_ACCOUNT')} ` : `${t('CX_AUTH_DONT_HAVE_ACCOUNT')} `}
            <a
              href={`${repubPath}/plus/auth/${isSignup ? 'login' : 'signup'}`}
              onClick={e => {
                e.preventDefault();
                openAtView(isForgotPassword ? VIEW_SIGNUP : isSignup ? VIEW_LOGIN : VIEW_SIGNUP);
              }}
            >
              {isSignup ? t('CX_AUTH_LOGIN') : t('CX_AUTH_SIGNUP')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

function Authentication({ children }: PropsWithChildren) {
  return (
    <div className={styles.authenticationContainer} data-testid="auth-modal">
      {children}
    </div>
  );
}

Authentication.AuthSection = AuthSection;
Authentication.InfoSection = InfoSection;

export { Authentication };
